/* eslint-disable i18next/no-literal-string -- .*/
import { TableWithFeatures } from '@/affiliate-dashboard/components/base/table-with-features';
import {
  type PayoutDtoFragment,
  useGetNextPayoutsQuery,
} from '@/affiliate-dashboard/graphql';
import { PageHeading } from '@/components/base/page-heading';
import { Skeleton } from '@/components/base/skeleton';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';
import invariant from 'tiny-invariant';
import { defaultColumns } from './columns';
import { PayoutDetailDrawer } from './payout-detail-drawer';

export function NextPayoutsTable({
  data,
  handlePageChange,
  totalPages,
  currentPage,
}: {
  data: (PayoutDtoFragment | null)[];
  handlePageChange: (page: number) => void;
  totalPages: number;
  currentPage: number;
}): JSX.Element {
  const intl = useIntl();
  const { t } = useTranslation();
  const columns = useMemo(() => defaultColumns(intl, { t }), [intl, t]);
  const [payoutId, setPayoutId] = useState<string | null>(null);

  return (
    <div className="w-full">
      <TableWithFeatures
        data={data}
        columns={columns}
        itemsPerPage={2}
        searchPlaceholder="Search commissions..."
        onPageChange={handlePageChange}
        totalPages={totalPages}
        page={currentPage}
        rowClick={(row) => {
          setPayoutId(row?.id ?? null);
        }}
        rowClassName="cursor-pointer"
      >
        <div className="w-full">
          <PageHeading className="w-full">Next Payouts</PageHeading>
        </div>
        <PayoutDetailDrawer
          onClose={() => {
            setPayoutId(null);
          }}
          payoutId={payoutId ?? ''}
          isOpen={Boolean(payoutId)}
        />
      </TableWithFeatures>
    </div>
  );
}

const PAGE_SIZE = 10;

export function NextPayoutsTableTablesWithData(): JSX.Element {
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading } = useGetNextPayoutsQuery({
    variables: {
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
  });

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  if (loading) {
    return (
      <div className="mx-auto flex h-full w-full flex-col gap-4">
        <Skeleton className="h-full max-h-32 w-full rounded-xl" />
      </div>
    );
  }

  invariant(data, 'Data should be defined');

  const nextPayouts = data?.nextPayouts?.payouts ?? [];
  const totalPages = data?.nextPayouts?.totalPageCount ?? 0;

  return (
    <div className="flex flex-col gap-10">
      <NextPayoutsTable
        handlePageChange={handlePageChange}
        data={nextPayouts}
        currentPage={currentPage}
        totalPages={totalPages}
      />
    </div>
  );
}
